import { useEffect, useState } from "react";

import { useAppState } from "./useAppState";

export interface AiSmeChatConfig {
  enabled: boolean;
  restrictTo?: {
    courseIds?: number[];
    userIds?: number[];
  };
}

interface Config {
  aiChatSme: AiSmeChatConfig;
}

export function useAiSmeChatConfig(): { isLoading: boolean; enabled: boolean } {
  const [config, setConfig] = useState<AiSmeChatConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { course, userId } = useAppState();

  // Fetch config on mount
  useEffect(() => {
    async function fetchConfig() {
      try {
        const response = await fetch("/assets/config.json");
        if (!response.ok) {
          throw new Error(`Failed to fetch config: ${response.statusText}`);
        }
        const data: Config = await response.json();
        setConfig(data.aiChatSme);
      } catch (err) {
        console.error("Error fetching AI SME chat config:", err);
        // Config remains null, so enabled will be false
      } finally {
        setIsLoading(false);
      }
    }

    fetchConfig();
  }, []);

  // Evaluate if AI chat is enabled based on config and current context
  const enabled = (() => {
    // If config hasn't loaded yet, return false
    if (!config) {
      return false;
    }

    // If feature is disabled globally, return false
    if (!config.enabled) {
      return false;
    }

    // If no restrictions, it's enabled
    if (!config.restrictTo) {
      return true;
    }

    const { courseIds, userIds } = config.restrictTo;

    // Check if restrictions are actually present and valid (non-empty arrays)
    const hasCourseRestriction = Array.isArray(courseIds) && courseIds.length > 0;
    const hasUserRestriction = Array.isArray(userIds) && userIds.length > 0;

    // If no valid restrictions exist, it's enabled
    if (!hasCourseRestriction && !hasUserRestriction) {
      return true;
    }

    // Check course restriction
    const courseMatches = hasCourseRestriction
      ? course?.trainingId !== undefined && courseIds.includes(course.trainingId)
      : true; // If no course restriction, consider it a match

    // Check user restriction
    const userMatches = hasUserRestriction ? userId !== undefined && userIds.includes(userId) : true;

    // Both restrictions must match if they're present
    return courseMatches && userMatches;
  })();

  return { isLoading, enabled };
}
